// src/components/SidebarAutoModule.tsx
//
// Composant Sidebar auto-généré pour les modules ajoutés via le catalogue
// avec `autoSidebar = true`. Le rendu est entièrement piloté par la BD
// (libellé, icône, couleur, route racine), sans aucun code React dédié au
// module — il suffit d'ajouter une ligne dans le catalogue + des types
// d'opération avec moduleCodes ⊇ [cle] pour qu'un nouveau processus
// apparaisse dans le menu de tous les utilisateurs souscrits au module.
//
// Anatomie d'un item auto :
//   ┌─ [icône] [libellé] [chevron]            ← entrée principale collapsible
//   │  ├─ [Home] Vue d'ensemble               ← lien direct sur routeBase
//   │  ├─ Demandes dynamiques (N)             ← séparateur / titre
//   │  ├─ [+] Type d'opération 1              ← issu de SidebarDynamicTypes
//   │  └─ [+] Type d'opération N
//
// Chaque item maintient son propre état d'ouverture (localStorage) pour
// préserver la navigation entre rechargements.
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import * as LucideIcons from 'lucide-react';
import { BarChart2, ChevronDown, ChevronRight, LayoutDashboard, Layers, Package, Users, PieChart } from 'lucide-react';
import SidebarDynamicTypes from './SidebarDynamicTypes';
import type { ModuleCatalogueDB } from '../services/package.service';
import { getCompositesForModule } from '../data/formTemplates';
import { useTypesOperation } from '../hooks/useTypesOperation';
import { useAuth } from '../context/AuthContext';
import { useLibellesMenu } from '../context/LibellesMenuContext';
import { useVocabulaire } from '../services/vocabulaire.service';

interface Props {
  module: ModuleCatalogueDB;
  sidebarOpen: boolean;
  closeMobile?: () => void;
}

/**
 * Récupère un composant icône Lucide à partir de son nom (string).
 * Tombe sur `LayoutDashboard` si l'icône n'existe pas.
 */
function getLucideIcon(iconName?: string | null): React.ComponentType<any> {
  if (!iconName) return LayoutDashboard;
  const Icon = (LucideIcons as any)[iconName];
  return Icon || LayoutDashboard;
}

const SidebarAutoModule: React.FC<Props> = ({ module, sidebarOpen, closeMobile }) => {
  const location = useLocation();
  // Libellés personnalisés par entité : module:<cle> et module:<cle>:lien:<path>
  const { libelle: lm } = useLibellesMenu();
  // Priorité des libellés : surcharge de menu saisie par l'entité, puis
  // vocabulaire métier de l'organisation, puis libellé du catalogue. Le
  // catalogue est COMMUN à tous les tenants d'une même base : le renommer y
  // aurait renommé « Fédération sportive » pour la FFG aussi.
  const { v } = useVocabulaire();
  const defautCatalogue = module.cle === 'federation' ? v.moduleFederation.libelle
    : module.cle === 'gestion_adherents' ? v.moduleAdherents.libelle
    : module.libelle;
  const libelleModule = lm('module:' + module.cle, defautCatalogue);
  const { hasPermission } = useAuth();
  const Icon = getLucideIcon(module.icone);
  const routeBase = module.routeBase || `/m/${module.cle}`;
  const storageKey = `sidebar_open_module_${module.cle}`;

  // Mai 2026 — Sortie Matériel : visible dans le sous-menu des modules BTP
  // (btp_chantier / btp_devis / autre btp_*) si l'utilisateur a un droit
  // de consultation ou création. Permet au gestionnaire de chantier de
  // créer ses demandes sans aller dans Stock.
  const showSortieMaterielLink = module.cle.startsWith('btp_')
    && (hasPermission('access_demande_sortie') || hasPermission('create_demande_sortie'));

  // Septembre 2026 — Factures fournisseur : épinglées sous le module Achats.
  // Le catalogue BD ne porte pas encore de `liens` pour ce module et il n'est
  // pas modifiable depuis le frontend ; on épingle donc côté code, comme cela
  // se fait déjà pour le module RH plus bas. Le droit testé est le MÊME que
  // celui exigé par la route (cf. AppRoutes), sans quoi le menu conduirait à
  // un « Accès non autorisé » atteint depuis sa propre navigation.
  const showFacturesFournisseurLinks = module.cle === 'achats'
    && hasPermission('access_factures_fournisseur');

  // Détermine si le module a des sous-items (composites + types dynamiques).
  // Si aucun, le module devient un lien direct (pas de chevron, pas de
  // "Vue d'ensemble" séparée — UX plus immédiate). Sinon il reste collapsible.
  const composites = getCompositesForModule(module.cle);
  const { types } = useTypesOperation();
  const hasDynamicTypes = (types || []).some(t =>
    t.actif !== false &&
    !t.systeme &&
    Array.isArray(t.moduleCodes) &&
    t.moduleCodes.includes(module.cle),
  );
  // Sans cet ajout, un module Achats dépourvu de composites et de types
  // dynamiques serait rendu en LIEN DIRECT (branche « cas 1 » ci-dessous) et
  // les entrées épinglées ne s'afficheraient jamais.
  const hasSubItems = composites.length > 0 || hasDynamicTypes || (module.liens?.length ?? 0) > 0
    || module.cle === 'electricite' || showFacturesFournisseurLinks;

  const [isOpen, setIsOpen] = useState<boolean>(() => {
    try {
      const v = localStorage.getItem(storageKey);
      // Par défaut : ouvert si on est déjà sur une route du module
      if (v === null) return location.pathname.startsWith(routeBase);
      return v === '1';
    } catch {
      return false;
    }
  });

  const toggle = () => {
    setIsOpen(prev => {
      const next = !prev;
      try { localStorage.setItem(storageKey, next ? '1' : '0'); } catch {}
      return next;
    });
  };

  const isActive = location.pathname === routeBase || location.pathname.startsWith(routeBase + '/');
  const accent = module.couleur || '#6366F1';

  // ── Cas 1 : module sans sous-items → lien direct (pas de wrapper) ─────────
  // Évite le faux-vide "Mon module > Vue d'ensemble" quand toutes les
  // fonctionnalités sont dans la page racine (onglets internes, etc.).
  if (!hasSubItems) {
    return (
      <div className="mb-1">
        <Link
          to={routeBase}
          onClick={() => { if (window.innerWidth < 1024 && closeMobile) closeMobile(); }}
          className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all group ${
            isActive ? 'text-white shadow-md' : 'text-gray-700 hover:bg-gray-50'
          }`}
          style={isActive ? { backgroundColor: accent } : undefined}
          title={module.description || libelleModule}
        >
          <div className="flex items-center space-x-3 min-w-0">
            <Icon className="h-5 w-5 flex-shrink-0" />
            {sidebarOpen && (
              <span className="text-sm font-medium leading-tight line-clamp-2">{libelleModule}</span>
            )}
          </div>
        </Link>
      </div>
    );
  }

  return (
    <div className="mb-1">
      {/* ── Entrée principale (collapsible) ── */}
      <div
        onClick={() => {
          if (sidebarOpen) toggle();
        }}
        className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all group ${
          isActive
            ? 'text-white shadow-md'
            : 'text-gray-700 hover:bg-gray-50'
        }`}
        style={isActive ? { backgroundColor: accent } : undefined}
        title={module.description || libelleModule}
      >
        <div className="flex items-center space-x-3 min-w-0">
          <Icon className="h-5 w-5 flex-shrink-0" />
          {sidebarOpen && (
            <span className="text-sm font-medium truncate">{libelleModule}</span>
          )}
        </div>
        {sidebarOpen && (
          <span className="flex-shrink-0">
            {isOpen ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </span>
        )}
      </div>

      {/* ── Sous-menu : route racine + composites + types DevMode ── */}
      {sidebarOpen && isOpen && (
        <div className="ml-8 mt-1 mb-2 space-y-1 border-l-2 border-gray-200 pl-2">
          <Link
            to={routeBase}
            onClick={() => { if (window.innerWidth < 1024 && closeMobile) closeMobile(); }}
            className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
              location.pathname === routeBase
                ? 'font-medium'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
            style={
              location.pathname === routeBase
                ? { color: accent, backgroundColor: `${accent}15` }
                : undefined
            }
          >
            <LayoutDashboard className="h-4 w-4" />
            <span>{lm('module:' + module.cle + ':lien:' + routeBase, "Vue d'ensemble")}</span>
          </Link>

          {/* Factures fournisseur — épinglées juste après Vue d'ensemble pour le module Achats */}
          {showFacturesFournisseurLinks && (
            <>
              <Link
                to="/factures-fournisseur"
                onClick={() => { if (window.innerWidth < 1024 && closeMobile) closeMobile(); }}
                className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                  location.pathname === '/factures-fournisseur' ? 'font-medium' : 'text-gray-600 hover:bg-gray-50'
                }`}
                style={location.pathname === '/factures-fournisseur' ? { color: accent, backgroundColor: `${accent}15` } : undefined}
              >
                <LucideIcons.FileText className="h-4 w-4 text-blue-500" />
                <span>Factures fournisseur</span>
              </Link>
              <Link
                to="/factures-fournisseur/a-rapprocher"
                onClick={() => { if (window.innerWidth < 1024 && closeMobile) closeMobile(); }}
                title="Factures présentant des écarts non arbitrés"
                className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                  location.pathname.startsWith('/factures-fournisseur/a-rapprocher') ? 'font-medium' : 'text-gray-600 hover:bg-gray-50'
                }`}
                style={location.pathname.startsWith('/factures-fournisseur/a-rapprocher') ? { color: accent, backgroundColor: `${accent}15` } : undefined}
              >
                <LucideIcons.Scale className="h-4 w-4 text-amber-500" />
                <span>Factures à rapprocher</span>
              </Link>
            </>
          )}

          {/* Demandes RH + Organisation — épinglés juste après Vue d'ensemble pour le module RH */}
          {module.cle === 'rh' && hasPermission('access_rh') && (
            <>
              <Link
                to="/rh/demandes"
                onClick={() => { if (window.innerWidth < 1024 && closeMobile) closeMobile(); }}
                className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                  location.pathname.startsWith('/rh/demandes') ? 'font-medium' : 'text-gray-600 hover:bg-gray-50'
                }`}
                style={location.pathname.startsWith('/rh/demandes') ? { color: accent, backgroundColor: `${accent}15` } : undefined}
              >
                <LucideIcons.Inbox className="h-4 w-4" />
                <span>Demandes RH</span>
              </Link>
              <Link
                to="/rh/organisation"
                onClick={() => { if (window.innerWidth < 1024 && closeMobile) closeMobile(); }}
                className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                  location.pathname.startsWith('/rh/organisation') ? 'font-medium' : 'text-gray-600 hover:bg-gray-50'
                }`}
                style={location.pathname.startsWith('/rh/organisation') ? { color: accent, backgroundColor: `${accent}15` } : undefined}
              >
                <LucideIcons.Building2 className="h-4 w-4" />
                <span>Organisation</span>
              </Link>
              <Link
                to="/rh/evenements"
                onClick={() => { if (window.innerWidth < 1024 && closeMobile) closeMobile(); }}
                className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                  location.pathname.startsWith('/rh/evenements') ? 'font-medium' : 'text-gray-600 hover:bg-gray-50'
                }`}
                style={location.pathname.startsWith('/rh/evenements') ? { color: accent, backgroundColor: `${accent}15` } : undefined}
              >
                <LucideIcons.Calendar className="h-4 w-4 text-pink-500" />
                <span>Événements</span>
              </Link>
              <Link
                to="/rh/annonces"
                onClick={() => { if (window.innerWidth < 1024 && closeMobile) closeMobile(); }}
                className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                  location.pathname.startsWith('/rh/annonces') ? 'font-medium' : 'text-gray-600 hover:bg-gray-50'
                }`}
                style={location.pathname.startsWith('/rh/annonces') ? { color: accent, backgroundColor: `${accent}15` } : undefined}
              >
                <LucideIcons.Bell className="h-4 w-4 text-purple-500" />
                <span>Annonces</span>
              </Link>
            </>
          )}

          {/* Liens statiques du module (sous-pages configurées dans le catalogue) */}
          {module.liens
            ?.filter(lien => !lien.permission || hasPermission(lien.permission))
            .filter(lien => {
              // Évite les doublons avec les liens épinglés ci-dessus pour le module RH
              if (module.cle === 'rh') {
                const p = lien.path.split('?')[0];
                if (p.startsWith('/rh/demandes') || p.startsWith('/rh/organisation')) return false;
              }
              return true;
            })
            .map(lien => {
              const LienIcon = lien.icone ? ((LucideIcons as any)[lien.icone] || Layers) : Layers;
              const lienPathname = lien.path.split('?')[0];
              const lienSearch = lien.path.includes('?') ? lien.path.slice(lien.path.indexOf('?')) : null;
              const isLienActive = location.pathname === lienPathname &&
                (!lienSearch || location.search === lienSearch || location.search.startsWith(lienSearch + '&'))
                || (!lienSearch && location.pathname.startsWith(lienPathname + '/'));
              return (
                <Link
                  key={lien.path}
                  to={lien.path}
                  onClick={() => { if (window.innerWidth < 1024 && closeMobile) closeMobile(); }}
                  title={lm('module:' + module.cle + ':lien:' + lien.path, lien.label)}
                  className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                    isLienActive ? 'font-medium' : 'text-gray-600 hover:bg-gray-50'
                  }`}
                  style={isLienActive ? { color: accent, backgroundColor: `${accent}15` } : undefined}
                >
                  <LienIcon className="h-4 w-4" />
                  <span className="leading-tight line-clamp-2">{lm('module:' + module.cle + ':lien:' + lien.path, lien.label)}</span>
                </Link>
              );
            })}

          {/* Statistiques patronales — visible uniquement sous gestion_activites, permission dédiée */}
          {module.cle === 'gestion_activites' && hasPermission('view_stats_patronales') && (
            <Link
              to="/stats-patronales"
              onClick={() => { if (window.innerWidth < 1024 && closeMobile) closeMobile(); }}
              className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                location.pathname === '/stats-patronales'
                  ? 'font-medium'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
              style={
                location.pathname === '/stats-patronales'
                  ? { color: accent, backgroundColor: `${accent}15` }
                  : undefined
              }
            >
              <PieChart className="h-4 w-4" />
              <span>Statistiques patronales</span>
            </Link>
          )}

          {/* Mai 2026 — Lien "Sortie Matériel" pour les modules BTP.
              Conditionné aux permissions demande-sortie pour respecter le RBAC. */}
          {showSortieMaterielLink && (
            <Link
              to="/stock/demandes-sortie"
              onClick={() => { if (window.innerWidth < 1024 && closeMobile) closeMobile(); }}
              title="Sortie Matériel — chantiers, programmes, consommations"
              className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                location.pathname.startsWith('/stock/demandes-sortie')
                  ? 'text-emerald-700 bg-emerald-50 font-medium'
                  : 'text-gray-600 hover:bg-emerald-50 hover:text-emerald-700'
              }`}
            >
              <Package className="h-4 w-4" />
              <span>Sortie Matériel</span>
            </Link>
          )}

          {/* Formulaires composite (multi-étapes, codés) déclarés pour ce module
              dans formTemplates.ts via composite.moduleCodes. Permet d'exposer
              automatiquement la "Nouvelle adhésion" sous "Gestion des Adhérents"
              sans coder le lien à la main. */}
          {getCompositesForModule(module.cle).map(tpl => {
            const CompIcon = tpl.composite?.icon
              ? ((LucideIcons as any)[tpl.composite.icon] || Layers)
              : Layers;
            const route = tpl.composite!.route;
            const isActive = location.pathname === route;
            return (
              <Link
                key={tpl.code}
                to={route}
                onClick={() => { if (window.innerWidth < 1024 && closeMobile) closeMobile(); }}
                title={tpl.description}
                className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                  isActive ? 'font-medium' : 'text-gray-600 hover:bg-emerald-50 hover:text-emerald-700'
                }`}
                style={isActive ? { color: '#065f46', backgroundColor: '#d1fae5' } : undefined}
              >
                <CompIcon className="h-4 w-4" />
                <span className="leading-tight line-clamp-2">{lm('module:' + module.cle + ':lien:' + route, tpl.label)}</span>
                <span className="ml-auto text-[9px] uppercase font-bold text-emerald-600 bg-emerald-50 px-1 rounded">
                  {tpl.composite?.nbEtapes ?? '?'}ét.
                </span>
              </Link>
            );
          })}

          {/* Inscription aux commissions — lien direct pour gestion_adherents */}
          {module.cle === 'gestion_adherents' && (() => {
            const path = '/demandes/nouvelle/INSCRIPTION_AUX_COMMISSIONS';
            const active = location.pathname === path;
            return (
              <Link
                to={path}
                onClick={() => { if (window.innerWidth < 1024 && closeMobile) closeMobile(); }}
                className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                  active ? 'font-medium' : 'text-gray-600 hover:bg-emerald-50 hover:text-emerald-700'
                }`}
                style={active ? { color: '#065f46', backgroundColor: '#d1fae5' } : undefined}
              >
                <Users className="h-4 w-4" />
                <span className="leading-tight line-clamp-2">Inscription aux commissions</span>
              </Link>
            );
          })()}

          {/* Composition des commissions — lien direct pour gestion_adherents */}
          {module.cle === 'gestion_adherents' && (() => {
            const path = '/adherents/composition-commissions';
            const active = location.pathname === path;
            return (
              <Link
                to={path}
                onClick={() => { if (window.innerWidth < 1024 && closeMobile) closeMobile(); }}
                className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                  active ? 'font-medium' : 'text-gray-600 hover:bg-emerald-50 hover:text-emerald-700'
                }`}
                style={active ? { color: '#065f46', backgroundColor: '#d1fae5' } : undefined}
              >
                <BarChart2 className="h-4 w-4" />
                <span className="leading-tight line-clamp-2">Composition des commissions</span>
              </Link>
            );
          })()}


          {/* États et indicateurs : ce qu'on édite pour un bureau ou
              une assemblée. */}
          {module.cle === 'gestion_adherents' && (() => {
            const path = '/adherents/etats';
            const active = location.pathname.startsWith(path);
            return (
              <Link
                to={path}
                onClick={() => { if (window.innerWidth < 1024 && closeMobile) closeMobile(); }}
                className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                  active ? 'font-medium' : 'text-gray-600 hover:bg-emerald-50 hover:text-emerald-700'
                }`}
                style={active ? { color: '#065f46', backgroundColor: '#d1fae5' } : undefined}
              >
                <BarChart2 className="h-4 w-4" />
                <span className="leading-tight line-clamp-2">États &amp; indicateurs</span>
              </Link>
            );
          })()}

          {/* Suivi des cotisations — l'écran de relance du secrétariat. */}
          {module.cle === 'gestion_adherents' && (() => {
            const path = '/adherents/cotisations';
            const active = location.pathname.startsWith(path);
            return (
              <Link
                to={path}
                onClick={() => { if (window.innerWidth < 1024 && closeMobile) closeMobile(); }}
                className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                  active ? 'font-medium' : 'text-gray-600 hover:bg-emerald-50 hover:text-emerald-700'
                }`}
                style={active ? { color: '#065f46', backgroundColor: '#d1fae5' } : undefined}
              >
                <BarChart2 className="h-4 w-4" />
                <span className="leading-tight line-clamp-2">Suivi des cotisations</span>
              </Link>
            );
          })()}

          {/* Types d'opération DevMode → liens vers /demandes/nouvelle/:code.
              Pour les modules transverses (électricité, réclamations…), les types
              sont groupés par catégorie (Recouvrement, Réclamation, Gestion
              compteur…) pour éviter une liste plate illisible. Les libellés de
              catégorie sont partagés via CATEGORIE_META : un module réutilise donc
              les MÊMES appellations que partout ailleurs. */}
          <SidebarDynamicTypes
            moduleCode={module.cle}
            groupByCategorie={
              module.cle === 'electricite' ||
              module.cle === 'facturation_electrique' ||
              module.cle === 'reclamations'
            }
            closeMobile={closeMobile}
          />
        </div>
      )}
    </div>
  );
};

export default SidebarAutoModule;
